import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { VentaService } from '../../services/venta.service';
import {
  MediosPagosDeOtrasFop,
  MonedasBilletesChequeVaccanceListado,
  ObtenerCustomanFormaPago,
  ObtenerCustomanFormData,
} from '../../models/pago.model';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilKeyChanged, map, takeUntil } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FormControlService } from 'src/app/shared/services/form-control.service';
import {
  CVacanceData,
  Cvaccance,
  OtrosFop,
  OtrosFopData,
} from '../../models/pago.model';
import { TranslateService } from 'src/app/shared/services/translate.service';

interface DialogData {
  placeholderValue: string;
  divisaSimboloDecimal: string;
  divisaDecimales: number;
  divisaPosicion: string;
  divisaSimbolo: string;
  divisaSimboloDerecha: string;
  divisaSimbSeparacionMiles: string;
  cambio: number;
  precioTotal: number;
  pagoTotal: number;
  pagoMultipleType: '0' | '1';
  checkVaccanceType: '0' | '1';
  otrasFopPagoForm: FormGroup;
  formaPagoData: ObtenerCustomanFormData[];
  checkVaccanceData: MonedasBilletesChequeVaccanceListado[];
}

enum ControlNameEnum {
  VACANCE = 'vacance',
  FOP_PAGO = 'fopPago',
  FOP_PAGO_BANK_NAME = 'bankName',
  FOP_PAGO_BANK_NUMBER = 'bankNumber',
  BANK_DETAILS = 'bankDetails',
  INPUTS = 'inputs',
  TITLE = 'title',
  PK_ID = 'pkId',
  FOP = 'FOP',
}

enum InputTypeEnum {
  NUMBER = 'number',
  TEXT = 'text',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
}

const priceValidatePattern = /^(?:\d+([.,]\d+)*|\d+)$/;
const numberValidatePattern = /^[0-9]+$/; // /^[\d]+$/;
const keepOnlyNumPattern = /[^.,\d\s]/g;
/** Cantidad máxima de dígitos en controles Chèque Vacances (Argonne / reglas caja). */
const VACANCE_MAX_DIGITS = 4;
const VACANCE_MAX_CANTIDAD = 9999;

interface InputValues {
  nombre: string;
  value?: number;
  pkId: string;
}

@Component({
  selector: 'ticketing-modal-pago',
  templateUrl: './modal-pago.component.html',
  styleUrls: ['./modal-pago.component.scss'],
})
export class ModalPagoComponent implements OnInit, OnDestroy {
  private destroy = new Subject();
  public fopData: ObtenerCustomanFormData[] = [];
  public pagoForm: FormGroup;
  public checkVacanceTotalValue = 0;
  public isConfirmBtnVacanseDisabled = true;
  public totalPrice = 0;
  public recibido = 0;
  public diferencia = 0;
  public totalVacance = 0;
  public total = 0;
  public controlNameEnum = ControlNameEnum;
  public inputTypeEnum = InputTypeEnum;
  public pagoMultiple: '0' | '1';

  public checkVacanceData: MonedasBilletesChequeVaccanceListado[] = [];

  public totalData: InputValues[] = [];

  constructor(
    private ventaService: VentaService,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private fb: FormBuilder,
    private formControlService: FormControlService,
    public dialogRef: MatDialogRef<ModalPagoComponent>,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.total = Math.abs(this.dialogData.precioTotal);
    this.totalPrice = this.totalPrice - this.dialogData.precioTotal;
    this.diferencia = this.totalPrice - this.recibido;
    this.pagoMultiple = this.dialogData.pagoMultipleType;

    if (!this.fopData.length && !this.checkVacanceData.length) {
      const dynamicForm = this.fb.group({});
      const bankDetails = new FormArray([]);

      if (!this.dialogData.otrasFopPagoForm) {
        this.dialogData.formaPagoData.forEach((input) => {
          input.tiposDePago.forEach((item, index) => {
            const bankDetailFormGroup = this.buildFormGroup(
              item.pkId,
              item.Descripcion,
              input.fop
            );

            bankDetails.push(bankDetailFormGroup);
          });
        });

        this.dialogData.checkVaccanceData.forEach((input) => {
          const control = new FormControl('0', [
            Validators.pattern(numberValidatePattern),
            Validators.maxLength(VACANCE_MAX_DIGITS),
          ]);
          dynamicForm.addControl(
            `${input.pkId}${this.controlNameEnum.VACANCE}`,
            control
          );
        });

        dynamicForm.controls['bankDetails'] = bankDetails;
        this.pagoForm = dynamicForm;
      }

      this.checkVacanceData = this.dialogData.checkVaccanceData.sort(
        (a, b) => a.orden - b.orden
      );

      if (this.dialogData.otrasFopPagoForm) {
        this.totalData = JSON.parse(sessionStorage.getItem('totalData'));
        this.pagoForm = this.generateFormPagoBasedOnPrevForm(
          this.dialogData.otrasFopPagoForm
        );

        this.markControlsAsDyrty(this.pagoForm);
        this.calculateTotalRecibido();
        this.calculateVacance();
      }

      this.fopData = this.dialogData.formaPagoData;
    }
  }

  public onInput(
    event: Event,
    controlName: string,
    pagosIndex: number,
    pagoInputsIndex: number | null,
    inputType?: string
  ): void {
    const element = event.target as HTMLInputElement;
    const control = this.getControlWithNumPad(
      event,
      controlName,
      pagosIndex,
      pagoInputsIndex
    );

    switch (inputType) {
      case 'number':
        control.setValue(element.value.replace(keepOnlyNumPattern, ''));
        break;
      case 'text':
        control.setValue(
          element.value.replace(/[^\w\u00C0-\u024F\u1E00-\u1EFF0-9 ]/g, '')
        );
        break;
      default:
        if (
          controlName === this.controlNameEnum.FOP_PAGO ||
          controlName === this.controlNameEnum.FOP_PAGO_BANK_NUMBER
        ) {
          control.setValue(element.value.replace(keepOnlyNumPattern, ''));
        }
        break;
    }
  }

  public onVacanseInput(value: string, controlName: string): void {
    this.updateCheckVaccanceData(value, controlName);
  }

  public focusInput(
    event: Event,
    controlName: string,
    pagosIndex: number,
    pagoInputsIndex?: number
  ): void {
    const control = this.getControlWithNumPad(
      event,
      controlName,
      pagosIndex,
      pagoInputsIndex
    );
    const value = (event.target as HTMLInputElement).value;
    if (controlName.includes(this.controlNameEnum.VACANCE)) {
      control.reset('');
      control.markAsPristine();
      if (this.pagoMultiple !== '1') {
        this.resetVaccanceInputs();
      }
      this.calculateVacance();
    } else {
      if (this.pagoMultiple !== '1') {
        this.clearElementsByMultiPagoTypeWithNum(pagosIndex);
      }
      this.updateFopPagoData(controlName);
    }

    this.formControlService.currentFormGroupValue.form.valueChanges
      .pipe(distinctUntilKeyChanged(controlName), takeUntil(this.destroy))
      .subscribe((formControl) => {
        let value = formControl[controlName];

        if (
          this.pagoMultiple !== '1' &&
          !controlName.includes(this.controlNameEnum.VACANCE)
        ) {
          this.clearElementsByMultiPagoTypeWithNum(pagosIndex);
          this.totalData = this.totalData.filter(
            (item) => item.pkId !== this.controlNameEnum.VACANCE
          );
          this.updateFopPagoData(controlName);
          control.markAsDirty();
        } else {
          this.updateFopPagoData(controlName);
          control.markAsDirty();
        }

        if (controlName.includes(this.controlNameEnum.VACANCE)) {
          if (value && typeof value !== 'number') {
            const digits = this.normalizeVacanceDigits(String(value));
            control.setValue(digits);
            control.markAsDirty();
          }

          if (/^[0-9]+$/.test(String(control.value ?? ''))) {
            this.calculateVacance();
            this.updateCheckVaccanceData(String(control.value ?? ''), controlName);
          }

          if (value === '' || value === '0') {
            control.reset('0');
            control.markAsPristine();
          }
        }
      });
  }

  private clearElementsByMultiPagoTypeWithNum(index: number): void {
    const bankDetailsArray = this.pagoForm.get(
      this.controlNameEnum.BANK_DETAILS
    ) as FormArray;

    if (bankDetailsArray && bankDetailsArray.length) {
      bankDetailsArray.controls.forEach((group, detailIndex) => {
        if (detailIndex !== index) {
          const getFopPagoControl = group.get(this.controlNameEnum.FOP_PAGO);
          const getBankNumberControl = group.get(
            this.controlNameEnum.FOP_PAGO_BANK_NUMBER
          );
          const getBankNameControl = group.get(
            this.controlNameEnum.FOP_PAGO_BANK_NAME
          );

          getFopPagoControl.setValue('');
          getFopPagoControl.markAsPristine();

          getBankNumberControl.setValue('');
          getBankNumberControl.markAsPristine();

          getBankNameControl.setValue('');
          getBankNameControl.markAsPristine();
        }
      });
    }
  }

  public getFormBankDetails(
    index: number,
    controlname: string
  ): AbstractControl {
    return this.pagoForm.controls['bankDetails']['controls'][index]['controls'][
      controlname
    ];
  }

  public acceptAll(): void {
    this.removeEmptyInputsAfterAccept();
    sessionStorage.setItem('totalData', JSON.stringify(this.totalData));

    const otrasFopData = this.generarMediosPagos();

    const passForm = JSON.parse(JSON.stringify(this.pagoForm.getRawValue()));
    this.dialogRef.close({
      totalValue: this.recibido,
      otrasFopPagoForm: passForm,
      accept: true,
      otrasFopData,
    });
  }

  public cancelAll(): void {
    sessionStorage.removeItem('totalData');
    this.dialogRef.close({
      totalValue: this.recibido,
      otrasFopPagoForm: null,
      accept: false,
    });
  }

  public addCantidadVacance(controlName: string, value: string): void {
    const control = this.getFormControl(controlName);

    const n = Number.parseInt(String(control.value ?? value ?? '0'), 10);
    const current = Number.isFinite(n) ? n : 0;
    if (current >= VACANCE_MAX_CANTIDAD) {
      return;
    }

    if (control.valid || /0/.test(value)) {
      control.setValue(`${current + 1}`);
      control.markAsDirty();
      this.updateControlState(controlName);
      this.calculateVacance();
    }
  }

  public addOtherFormPay(tipoDePagoIndex: number): void {
    const getBankDetailsControls = this.pagoForm.get(
      'bankDetails'
    ) as FormArray;
    const inputsControls = getBankDetailsControls
      .at(tipoDePagoIndex)
      .get('inputs') as FormArray;

    const newInputs = this.fb.group({
      [this.controlNameEnum.FOP_PAGO]: [
        '',
        [Validators.pattern(priceValidatePattern)],
      ],
      [this.controlNameEnum.FOP_PAGO_BANK_NUMBER]: [
        '',
        [Validators.pattern(numberValidatePattern), Validators.maxLength(20)],
      ],
      [this.controlNameEnum.FOP_PAGO_BANK_NAME]: [
        '',
        [Validators.maxLength(100)],
      ],
    });

    inputsControls.push(newInputs);
  }

  public removeFormPay(pagosIndex: number, pagoInputsIndex: number): void {
    const control = this.geBtankDetailsInputsControls(pagosIndex) as FormArray;
    control.removeAt(pagoInputsIndex);
    this.totalData = this.generateTotalData();
    this.calculateTotalRecibido();
  }

  public confirmVacanse(): void {
    if (this.checkVacanceTotalValue) {
      this.totalVacance = this.checkVacanceTotalValue;
      const newVacance = {
        nombre: this.translateService.instant('CHEQUE_VACCANCES'),
        value: this.totalVacance,
        pkId: this.controlNameEnum.VACANCE,
      };
      this.totalData = this.totalData
        .filter((item) => item.pkId !== this.controlNameEnum.VACANCE)
        .concat(newVacance);

      if (this.pagoMultiple !== '1') {
        this.pagoForm.controls['bankDetails'] = this.resetBankDetailsInputs();
        this.totalData = this.totalData.filter(
          (item) => item.pkId === this.controlNameEnum.VACANCE
        );
      }

      this.calculateTotalRecibido();
    }
  }

  public focusOutInput(
    event: Event,
    controlName: string,
    pagosIndex: number,
    pagoInputsIndex?: number,
    controlPath?: string
  ): void {
    const element = event.target as HTMLInputElement;
    const control = this.getControlWithNumPad(
      event,
      controlName,
      pagosIndex,
      pagoInputsIndex
    );
    if (element.value && control.valid) {
      let getOnlyNumber = Number(element.value.replace(',', '.'));
      let modifyedValue = this.convertNumberToPrice(getOnlyNumber);

      if (this.pagoMultiple !== '1') {
        let getAllPrice =
          getOnlyNumber < Math.abs(this.totalPrice)
            ? Math.abs(this.totalPrice)
            : getOnlyNumber;
        modifyedValue = this.convertNumberToPrice(getAllPrice);

        // this.pagoForm.controls['bankDetails'] = this.resetBankDetailsInputs();
        this.clearElementsByMultiPagoTypeWithNum(pagosIndex);
        this.resetVaccanceInputs();
        this.totalData = this.totalData.filter(
          (item) => item.pkId !== this.controlNameEnum.VACANCE
        );

        (
          this.getBankDetailsControls()['controls'][pagosIndex] as FormGroup
        ).controls['fopPago'].setValue(modifyedValue);
        this.updateFopPagoData(controlName);
      } else {
        control.setValue(modifyedValue, { emitEvent: false });
        control.setErrors(null);
        this.updateFopPagoData(controlName);
      }
    }
  }

  public removeSelectedPago(pkId: string, selectedIndex: number): void {
    const clearFormArray = this.fb.array([]);

    if (pkId !== this.controlNameEnum.VACANCE) {
      this.getBankDetailsControls()['controls'].forEach(
        (control: FormGroup) => {
          if (control['controls']['pkId'].value === pkId) {
            control['controls']['fopPago'].reset();
            control['controls']['fopPago'].markAsPristine();

            control['controls']['bankNumber'].reset();
            control['controls']['bankNumber'].markAsPristine();

            control['controls']['bankName'].reset();
            control['controls']['bankName'].markAsPristine();

            control.setControl('inputs', clearFormArray);
          }
        }
      );

      this.totalData = this.generateTotalData();
    } else {
      this.totalData.splice(selectedIndex, 1);

      this.checkVacanceData.forEach((cheques) => {
        if (
          this.pagoForm.controls[
            `${cheques.pkId}${this.controlNameEnum.VACANCE}`
          ] !== undefined &&
          this.pagoForm.controls[
            `${cheques.pkId}${this.controlNameEnum.VACANCE}`
          ].value !== '0'
        ) {
          this.pagoForm.controls[
            `${cheques.pkId}${this.controlNameEnum.VACANCE}`
          ].setValue('0');
          this.pagoForm.controls[
            `${cheques.pkId}${this.controlNameEnum.VACANCE}`
          ].markAsPristine();
        }
      });
    }
    this.calculateTotalRecibido();
  }

  public getFormControl(controlname: string): AbstractControl {
    return this.pagoForm.controls[controlname];
  }

  private generarMediosPagos(): MediosPagosDeOtrasFop {
    const otrasFop = this.generarOtrasFopPagos();
    const checkVaccance = this.generarCVaccanceData();

    return {
      otrasFop,
      checkVaccance,
    };
  }

  private generarOtrasFopPagos(): OtrosFopData[] {
    const bankDetailsArray = this.pagoForm.get(
      this.controlNameEnum.BANK_DETAILS
    ) as FormArray;
    const otrasFopData: OtrosFopData[] = [];

    if (bankDetailsArray && bankDetailsArray.length) {
      bankDetailsArray.controls.forEach((group, detailIndex) => {
        const inputsArray = group.get(this.controlNameEnum.INPUTS) as FormArray;
        const getFopPagoControl = group.get(this.controlNameEnum.FOP_PAGO);
        const getBankNumberControl = group.get(
          this.controlNameEnum.FOP_PAGO_BANK_NUMBER
        );
        const getBankName = group.get(this.controlNameEnum.FOP_PAGO_BANK_NAME);
        const controlFop = group.get(this.controlNameEnum.FOP);
        const getPkIDControl = group.get(this.controlNameEnum.PK_ID);
        const findTotalImporte = this.totalData.find(
          (item) => item.pkId === getPkIDControl.value
        );

        const otrasFop = {
          FOP: (controlFop && controlFop.value) || '',
          Importe: (findTotalImporte && findTotalImporte.value) || 0,
          Cheques: [],
        };

        if (
          getFopPagoControl &&
          getFopPagoControl.value !== '' &&
          !/^[0]*$/.test(getFopPagoControl.value) &&
          getFopPagoControl.value !== '0,00' &&
          getFopPagoControl.value !== null
        ) {
          const getOnlyNumber = Number(
            getFopPagoControl.value.replace(',', '.')
          );

          const cheque: OtrosFop = {
            Importe: getOnlyNumber || 0,
            NombreBanco: (getBankName && getBankName.value) || null,
            NumeroCheque:
              (getBankNumberControl && getBankNumberControl.value) || null,
          };

          otrasFop.Cheques.push(cheque);
        }

        if (inputsArray && inputsArray.length) {
          inputsArray.controls.forEach((group, i) => {
            const controlGroup = (group as FormGroup).controls;
            const controlFopPago = controlGroup[this.controlNameEnum.FOP_PAGO];
            const controlBankNumber =
              controlGroup[this.controlNameEnum.FOP_PAGO_BANK_NUMBER];
            const controlBankName =
              controlGroup[this.controlNameEnum.FOP_PAGO_BANK_NAME];

            if (
              controlFopPago &&
              controlFopPago.value !== '' &&
              !/^[0]*$/.test(controlFopPago.value) &&
              controlFopPago.value !== '0,00' &&
              controlFopPago.value !== null
            ) {
              const getOnlyNumber = Number(
                controlFopPago.value.replace(',', '.')
              );
              const cheque: OtrosFop = {
                Importe: getOnlyNumber || 0,
                NombreBanco: (controlBankName && controlBankName.value) || null,
                NumeroCheque:
                  (controlBankNumber && controlBankNumber.value) || null,
              };

              otrasFop.Cheques.push(cheque);
            }
          });
        }

        otrasFopData.push(otrasFop);
      });
    }

    return otrasFopData;
  }

  private generarCVaccanceData(): CVacanceData {
    const cVaccanceData: CVacanceData = {
      FOP: 'CV',
      Importe: this.checkVacanceTotalValue || 0,
      CVaccance: [],
    };

    if (this.checkVacanceTotalValue > 0) {
      this.checkVacanceData.forEach((item, i) => {
        const control =
          this.pagoForm.controls[item.pkId + this.controlNameEnum.VACANCE];

        if (
          control &&
          control.value !== 0 &&
          control.value !== '' &&
          control.value !== '0'
        ) {
          const check: Cvaccance = {
            MonedasBilletesChequeVaccanceId: item.pkId,
            Cantidad: Number(control.value),
            ImporteUnitario: item.valor,
          };

          cVaccanceData.CVaccance.push(check);
        }
      });
    }

    return cVaccanceData;
  }

  private buildFormGroup(
    pkId?: string,
    title?: string,
    fopType: string = '',
    fopPagoValue: string = ''
  ): FormGroup {
    const group = this.fb.group({
      [this.controlNameEnum.FOP_PAGO]: [
        fopPagoValue,
        [Validators.pattern(priceValidatePattern)],
      ],
      [this.controlNameEnum.FOP_PAGO_BANK_NUMBER]: [
        '',
        [Validators.pattern(numberValidatePattern), Validators.maxLength(20)],
      ],
      [this.controlNameEnum.FOP_PAGO_BANK_NAME]: [
        '',
        [Validators.maxLength(100)],
      ],
      FOP: fopType,
      inputs: new FormArray([]),
    });

    if (pkId) {
      const control = new FormControl(pkId);
      group.addControl('pkId', control);
    }

    if (title) {
      const control = new FormControl(title);
      group.addControl('title', control);
    }

    return group;
  }

  private generateFormPagoBasedOnPrevForm(data, key?: string): any {
    if (data === null || data === undefined) {
      return this.fb.control(null);
    }

    if (Array.isArray(data)) {
      return this.fb.array(
        data.map((item) => this.generateFormPagoBasedOnPrevForm(item))
      );
    } else if (typeof data === 'object') {
      const group: { [key: string]: any } = {};

      Object.keys(data).forEach((key) => {
        group[key] = this.generateFormPagoBasedOnPrevForm(data[key], key);
      });

      return this.fb.group(group);
    } else {
      let validators;
      validators =
        key &&
        key !== this.controlNameEnum.FOP_PAGO_BANK_NAME &&
        !key.includes(this.controlNameEnum.VACANCE)
          ? [Validators.pattern(priceValidatePattern)]
          : null;
      validators =
        key && key.includes(this.controlNameEnum.VACANCE)
          ? [
              Validators.pattern(numberValidatePattern),
              Validators.maxLength(VACANCE_MAX_DIGITS),
            ]
          : validators;

      return this.fb.control(data, validators);
    }
  }

  private markControlsAsDyrty(
    control: FormGroup | FormGroup | FormArray
  ): void {
    if (control instanceof FormControl) {
      if (control.value && control.value !== '0') {
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    } else if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach((key) => {
        const childControl = control.get(key) as FormGroup | FormArray;

        if (childControl) {
          this.markControlsAsDyrty(childControl);
        }
      });
    }
  }

  private clearAllInputs(
    formGroup: FormGroup,
    key?: string,
    currentPath?: string,
    keepPath?: string
  ): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      const newPath = currentPath ? `${currentPath}.${key}` : key;

      if (control instanceof FormControl && key !== 'title' && key !== 'pkId') {
        if (newPath !== keepPath) {
          control.setValue('');
          control.markAsPristine();
        }
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.clearAllInputs(control as FormGroup, key, newPath, keepPath);
      }
    });
  }

  private getBankDetailsControls() {
    return this.pagoForm.get('bankDetails');
  }

  private geBtankDetailsInputsControls(inputIndex: number) {
    return this.getBankDetailsControls()
      ['controls'].at(inputIndex)
      .get('inputs');
  }

  private updateFopPagoData(controlName: string): void {
    if (
      controlName === this.controlNameEnum.FOP_PAGO ||
      controlName === this.controlNameEnum.VACANCE
    ) {
      this.totalData = this.generateTotalData();
      this.calculateTotalRecibido();
    }
  }

  private getControlWithNumPad(
    event: Event,
    controlName: string,
    pagosIndex: number,
    pagoInputsIndex: number | null | undefined
  ): FormControl {
    let control: FormControl;

    const element = event.target as HTMLInputElement;
    const categoryType = element.getAttribute('category');
    element.setAttribute('formcontrolname', controlName);

    if (controlName.includes(this.controlNameEnum.VACANCE)) {
      this.formControlService.setCurrentFormGroup(
        this.pagoForm,
        element,
        categoryType
      );
      control = this.pagoForm.controls[controlName] as FormControl;
    } else {
      if (pagoInputsIndex !== undefined && pagoInputsIndex !== null) {
        this.formControlService.setCurrentFormGroup(
          this.geBtankDetailsInputsControls(pagosIndex)['controls'][
            pagoInputsIndex
          ],
          element,
          element.getAttribute('category')
        );
        control = this.geBtankDetailsInputsControls(pagosIndex)['controls'][
          pagoInputsIndex
        ]['controls'][controlName] as FormControl;
      } else {
        this.formControlService.setCurrentFormGroup(
          this.getBankDetailsControls()['controls'][pagosIndex],
          element,
          element.getAttribute('category')
        );
        control = this.getBankDetailsControls()['controls'][pagosIndex][
          'controls'
        ][controlName] as FormControl;
      }
    }

    return control;
  }

  private normalizeVacanceDigits(raw: string): string {
    let digits = String(raw ?? '')
      .replace(/[^0-9]/g, '')
      .replace(/^0+(?=\d)/g, '');
    if (digits.length > VACANCE_MAX_DIGITS) {
      digits = digits.slice(0, VACANCE_MAX_DIGITS);
    }
    const num = Number.parseInt(digits, 10);
    if (Number.isFinite(num) && num > VACANCE_MAX_CANTIDAD) {
      return String(VACANCE_MAX_CANTIDAD);
    }
    return digits;
  }

  private updateCheckVaccanceData(value: string, controlName: string): void {
    const control = this.getFormControl(controlName);

    if (value && typeof value !== 'number') {
      const digits = this.normalizeVacanceDigits(value);
      control.setValue(digits);
    }

    if (/^[0-9]+$/.test(String(control.value ?? ''))) {
      this.calculateVacance();
    }

    if (value === '' || value === '0') {
      control.reset('0');
      control.markAsPristine();
    }

    if (this.isConfirmBtnVacanseDisabled) {
      this.totalVacance = this.checkVacanceTotalValue;
      this.totalData = this.totalData.filter(
        (item) => item.pkId !== this.controlNameEnum.VACANCE
      );
      this.calculateTotalRecibido();
    }
  }

  private generateTotalData(): InputValues[] {
    const getVacance = this.totalData.find(
      (item) => item.pkId === this.controlNameEnum.VACANCE
    );
    const bankDetailsArray = this.pagoForm.get(
      this.controlNameEnum.BANK_DETAILS
    ) as FormArray;
    const data: InputValues[] = [];

    if (bankDetailsArray && bankDetailsArray.length) {
      bankDetailsArray.controls.forEach((group, detailIndex) => {
        const inputsArray = group.get(this.controlNameEnum.INPUTS) as FormArray;
        const getFopPagoControl = group.get(this.controlNameEnum.FOP_PAGO);
        const getTitleControl = group.get(this.controlNameEnum.TITLE);
        const getPkIdControl = group.get(this.controlNameEnum.PK_ID);

        let inputValue: InputValues = {
          nombre: (getTitleControl && getTitleControl.value) || '',
          value: 0,
          pkId: (getPkIdControl && getPkIdControl.value) || '',
        };

        if (
          getFopPagoControl &&
          getFopPagoControl.value !== '' &&
          !/^[0]*$/.test(getFopPagoControl.value) &&
          getFopPagoControl.value !== '0,00' &&
          getFopPagoControl.value !== null
        ) {
          const getOnlyNumber = Number(
            getFopPagoControl.value.replace(',', '.')
          );
          inputValue = { ...inputValue, value: getOnlyNumber };
        }

        if (inputsArray && inputsArray.length) {
          for (let i = inputsArray.length - 1; i >= 0; i--) {
            const controlGroup = inputsArray.at(i) as FormGroup;
            const control =
              controlGroup.controls[this.controlNameEnum.FOP_PAGO];

            if (
              control &&
              control.value !== '' &&
              !/^[0]*$/.test(control.value) &&
              control.value !== '0,00' &&
              control.value !== null
            ) {
              const getOnlyNumber = Number(control.value.replace(',', '.'));
              inputValue = {
                ...inputValue,
                value: inputValue.value + getOnlyNumber,
              };
            }
          }
        }

        if (inputValue.value !== 0) {
          data.push(inputValue);
        }
      });
    }

    if (getVacance) {
      data.push(getVacance);
    }

    return data;
  }

  private removeEmptyInputsAfterAccept(): void {
    const bankDetailsArray = this.pagoForm.get(
      this.controlNameEnum.BANK_DETAILS
    ) as FormArray;

    if (bankDetailsArray && bankDetailsArray.length) {
      bankDetailsArray.controls.forEach((group, detailIndex) => {
        const inputsArray = group.get(this.controlNameEnum.INPUTS) as FormArray;
        const getFopPagoControl = group.get(this.controlNameEnum.FOP_PAGO);

        if (
          (getFopPagoControl && getFopPagoControl.value === '') ||
          /^[0]*$/.test(getFopPagoControl.value) ||
          getFopPagoControl.value === '0,00'
        ) {
          getFopPagoControl.setValue('');
          getFopPagoControl.markAsPristine();
        }

        if (inputsArray && inputsArray.length) {
          for (let i = inputsArray.length - 1; i >= 0; i--) {
            const controlGroup = inputsArray.at(i) as FormGroup;
            const control =
              controlGroup.controls[this.controlNameEnum.FOP_PAGO];

            if (
              (control && control.value === '') ||
              /^[0]*$/.test(control.value) ||
              control.value === '0,00'
            ) {
              inputsArray.removeAt(i);
            }
          }
        }
      });
    }
  }

  private resetBankDetailsInputs(
    fopPagoValue: string = '',
    bankDetailsIndex?: number
  ): FormArray {
    const bankDetails = new FormArray([]);

    this.fopData.forEach((input, idx) => {
      input.tiposDePago.forEach((item, index) => {
        const bankDetailFormGroup = this.buildFormGroup(
          item.pkId,
          item.Descripcion,
          input.fop,
          idx === bankDetailsIndex ? fopPagoValue : ''
        );

        bankDetails.push(bankDetailFormGroup);
      });
    });

    return bankDetails;
  }

  private resetVaccanceInputs(): void {
    this.checkVacanceData.forEach((item) => {
      const getControl =
        this.pagoForm.controls[item.pkId + this.controlNameEnum.VACANCE];

      if (getControl) {
        getControl.setValue('');
        getControl.markAsPristine();
      }
    });

    this.checkVacanceTotalValue = 0;
    this.isConfirmBtnVacanseDisabled = true;
  }

  private convertNumberToPrice(value: number): string {
    return value.toFixed(this.dialogData.divisaDecimales).replace('.', ',');
  }

  private updateControlState(controlName: string): void {
    this.getFormControl(controlName).updateValueAndValidity();
  }

  private calculateVacance(): void {
    const getSumVacance = this.checkVacanceData.reduce((sum, currVac) => {
      const getControl =
        this.pagoForm.controls[currVac.pkId + this.controlNameEnum.VACANCE];

      sum +=
        getControl && getControl.valid && getControl.value
          ? getControl.value * currVac.valor
          : 0;

      return sum;
    }, 0);

    if (this.pagoMultiple === '1') {
      this.isConfirmBtnVacanseDisabled = !getSumVacance;
    } else {
      if (getSumVacance >= Math.abs(this.totalPrice)) {
        this.isConfirmBtnVacanseDisabled = false;
      } else {
        this.isConfirmBtnVacanseDisabled = true;
      }
    }

    this.checkVacanceTotalValue = getSumVacance;
  }

  private calculatePagos(): number {
    return this.totalData.reduce((sum, curr) => {
      sum += curr.value ? +curr.value : 0;

      return sum;
    }, 0);
  }

  private calculateTotalRecibido() {
    this.recibido = this.calculatePagos();
    this.diferencia = this.totalPrice + this.recibido;
  }

  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
